import { useState } from 'react'
import { axiosInstance } from '../helpers'
import { toast } from 'react-toastify'

export interface QuestionConfig {
    id: number
    apartment: number
    question_text: string
    question_type: 'RATING' | 'TEXT' | 'YES_NO'
    required: boolean
    order: number
    active: boolean
    created_date: string
    modified_date: string
}

export interface QuestionConfigPayload {
    apartment_id: number
    question_text: string
    question_type: 'RATING' | 'TEXT' | 'YES_NO'
    required?: boolean
    order?: number
    active?: boolean
}

export const useQuestionConfig = () => {
    const [questions, setQuestions] = useState<QuestionConfig[]>([])
    const [loading, setLoading] = useState(false)

    const getQuestions = async (apartmentId: number) => {
        setLoading(true)
        try {
            const res = await axiosInstance.get('survey/question-config/', {
                params: { apartment_id: apartmentId },
            })
            setQuestions(res.data)
        } catch {
            toast.error('Failed to load survey questions.')
        } finally {
            setLoading(false)
        }
    }

    const createQuestion = async (payload: QuestionConfigPayload) => {
        try {
            const res = await axiosInstance.post(
                'survey/question-config/',
                payload,
            )
            setQuestions((prev) => [...prev, res.data])
            toast.success('Question added.')
            return res.data
        } catch {
            toast.error('Failed to add question.')
            return null
        }
    }

    const updateQuestion = async (
        id: number,
        apartmentId: number,
        payload: Partial<Omit<QuestionConfigPayload, 'apartment_id'>>,
    ) => {
        try {
            const res = await axiosInstance.patch(
                `survey/question-config/${id}/?apartment_id=${apartmentId}`,
                payload,
            )
            setQuestions((prev) =>
                prev.map((q) => (q.id === id ? res.data : q)),
            )
            return res.data
        } catch {
            toast.error('Failed to update question.')
            return null
        }
    }

    const deleteQuestion = async (id: number, apartmentId: number) => {
        try {
            await axiosInstance.delete(
                `survey/question-config/${id}/?apartment_id=${apartmentId}`,
            )
            setQuestions((prev) => prev.filter((q) => q.id !== id))
            toast.success('Question deleted.')
        } catch {
            toast.error('Failed to delete question.')
        }
    }

    return {
        questions,
        setQuestions,
        loading,
        getQuestions,
        createQuestion,
        updateQuestion,
        deleteQuestion,
    }
}
